import { useCallback, useEffect, useState } from "react";
import { Search, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUS_OPTIONS = ["", "confirmed", "dispatched", "en_route", "chauffeur_arrived", "passenger_loaded", "completed", "cancelled"];
const SERVICE_TYPES = ["", "point_to_point", "hourly", "airport"];

export default function AdminBookings() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = { page, page_size: pageSize };
      if (status) params.status = status;
      if (serviceType) params.service_type = serviceType;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/bookings", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  }, [page, pageSize, status, serviceType, q]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const exportCsv = () => {
    const cols = ["id","pickup_datetime","client_name","client_email","driver_name","vehicle_category","status","service_type","total_price","pickup_address","dropoff_address"];
    const csv = [cols.join(",")].concat(items.map(b => cols.map(c => JSON.stringify((b[c] ?? "")).replaceAll(",", " ")).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `phantasm-bookings-page${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-bookings" className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-2">Bookings</div>
          <h1 className="font-serif text-3xl font-semibold tracking-tighter">All reservations</h1>
        </div>
        <button onClick={exportCsv} data-testid="admin-bookings-export" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-4 py-2.5">
          <Download size={14} strokeWidth={1.5} /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E9AA6]" />
          <input
            type="text" placeholder="Search client / address…" value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
            data-testid="admin-bookings-search"
            className="w-full bg-[#121214] border border-white/15 pl-9 pr-3 py-2.5 text-sm focus:border-white focus:outline-none"
          />
        </div>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          data-testid="admin-bookings-status-filter"
          className="bg-[#121214] border border-white/15 px-3 py-2.5 text-sm focus:border-white focus:outline-none">
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All statuses"}</option>)}
        </select>
        <select value={serviceType} onChange={(e) => { setPage(1); setServiceType(e.target.value); }}
          data-testid="admin-bookings-service-filter"
          className="bg-[#121214] border border-white/15 px-3 py-2.5 text-sm focus:border-white focus:outline-none">
          {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All services"}</option>)}
        </select>
        <div className="flex items-center justify-end text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">
          {total} result{total === 1 ? "" : "s"}
        </div>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      <div className="bg-[#121214]/60 border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-bookings-table">
          <thead className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
            <tr className="border-b border-white/10">
              <th className="text-left px-5 py-3">Pickup</th>
              <th className="text-left px-5 py-3">Client</th>
              <th className="text-left px-5 py-3">Route</th>
              <th className="text-left px-5 py-3">Vehicle</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">No bookings</td></tr>}
            {items.map(b => (
              <tr key={b.id} data-testid={`admin-bookings-row-${b.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3 font-light whitespace-nowrap">{new Date(b.pickup_datetime).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                <td className="px-5 py-3 font-light">{b.client_name}<div className="text-[10px] text-[#8E9AA6]">{b.client_email}</div></td>
                <td className="px-5 py-3 font-light">
                  <div className="max-w-xs truncate">{b.pickup_address}</div>
                  {b.dropoff_address && <div className="text-[#8E9AA6] text-xs max-w-xs truncate">→ {b.dropoff_address}</div>}
                </td>
                <td className="px-5 py-3 font-light">{b.vehicle_category.replace(/_/g, " ")}</td>
                <td className="px-5 py-3"><span className="text-[10px] uppercase tracking-[0.25em] text-[#0B0B0C] bg-[#E6E6E6] px-2 py-0.5">{b.status.replace(/_/g, " ")}</span></td>
                <td className="px-5 py-3 text-right tabular-nums">${b.total_price.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-[#8E9AA6]">
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
          data-testid="admin-bookings-prev" className="uppercase tracking-[0.25em] hover:text-white disabled:opacity-30 px-3 py-2">← Prev</button>
        <div>Page {page} of {totalPages}</div>
        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
          data-testid="admin-bookings-next" className="uppercase tracking-[0.25em] hover:text-white disabled:opacity-30 px-3 py-2">Next →</button>
      </div>
    </div>
  );
}
